import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Upload, User, GraduationCap, MapPin, Sparkles, Loader2 } from 'lucide-react';
import { AUSTRIAN_STATES, getDistrictsForState, getGemeindenForDistrict, getLocationCoordinates } from '../data/austrianLocations';
import { ResidenceLocationFields } from '../components/profile/ResidenceLocationFields';
import { PreferredWorkLocationsField } from '../components/profile/PreferredWorkLocationsField';
import { JobAreasField } from '../components/profile/JobAreasField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Vollständige Liste aller Studienrichtungen in Österreich
const AUSTRIAN_STUDIES = [
  'Advanced Nursing Practice', 'Ägyptologie', 'Afrikawissenschaften', 'Agrarmanagement',
  'Agrarpädagogik', 'Agrartechnologie', 'Agrarwissenschaften', 'Alte Geschichte',
  'Amerikanistik', 'Anglistik', 'Anthropologie', 'Arabistik', 'Archäologie', 'Architektur',
  'Artificial Intelligence', 'Asienwissenschaften', 'Astronomie', 'Atmosphärenwissenschaften',
  'Auditing', 'Automatisierungstechnik', 'Automotive', 'Bahntechnologie', 'Banking & Finance',
  'Bauingenieurwesen', 'Betriebswirtschaft', 'Bildende Kunst', 'Bildungswissenschaft',
  'Biochemie', 'Bioinformatik', 'Biologie', 'Biomedizin', 'Biomedizinische Analytik',
  'Biophysik', 'Biotechnologie', 'Botanik', 'Bühnenbild', 'Business Administration',
  'Business Management', 'Business Studies', 'Change Management', 'Chemie',
  'Chinesische Medizin', 'Coaching & Beratung', 'Computer Science', 'Controlling',
  'Data Science', 'Diätologie', 'Digital Business', 'Digitale Medien', 'Dirigieren',
  'Dolmetschen/Übersetzen', 'E-Commerce', 'E-Health', 'Elektrotechnik', 'Embedded Systems',
  'Energiemanagement', 'Energietechnik', 'Erneuerbare Energien', 'Entrepreneurship',
  'Erdwissenschaften', 'Ergotherapie', 'Ernährungsmedizin', 'Ernährungswissenschaften',
  'Erziehungswissenschaften', 'Ethik', 'Ethnologie', 'Eventmanagement', 'Facility Management',
  'Fahrzeugtechnik', 'Fennistik', 'Film & Fernsehen', 'Fitnesswissenschaft', 'Forensik',
  'Forstwirtschaft', 'Fotografie', 'Game Design', 'Gebäudetechnik', 'Geisteswissenschaften',
  'Gender Studies', 'General Management', 'Geodäsie', 'Geographie', 'Geoinformatik',
  'Geoinformation', 'Geologie', 'Geowissenschaften', 'Germanistik', 'Gerontologie',
  'Gesang', 'Gesangspädagogik', 'Geschichte', 'Gesundheits- und Krankenpflege',
  'Gesundheitsförderung', 'Gesundheitsmanagement', 'Gesundheitspädagogik',
  'Gesundheitspsychologie', 'Gesundheitstourismus', 'Gesundheitswissenschaften',
  'Grafikdesign', 'Handelsmanagement', 'Hebammen', 'Heilpädagogik', 'Hospitality Management',
  'Hotelmanagement', 'Hungarologie', 'Immobilienmanagement', 'Industrial Design',
  'Industrial Engineering', 'Industrial Management', 'Informatik', 'Informationsmanagement',
  'Informationstechnik', 'Innenarchitektur', 'Innovationsmanagement', 'Instrumentalpädagogik',
  'Instrumentalstudium', 'Internationale Beziehungen', 'Internationales Management',
  'IT Management', 'IT Security', 'Japanologie', 'Journalismus', 'Judaistik', 'Jus',
  'Kirchenmusik', 'Kommunikationsdesign', 'Kommunikationsmanagement',
  'Kommunikationswissenschaft', 'Komposition', 'Konservierung & Restaurierung',
  'Korrépetition', 'Krankenhausmanagement', 'Kriminologie', 'Kulturmanagement',
  'Kulturwissenschaften', 'Kunst', 'Kunstgeschichte', 'Kunstwissenschaft', 'Kunsttherapie',
  'Lehramt', 'Lebensmitteltechnologie', 'Linguistik', 'Literaturwissenschaft', 'Logistik',
  'Logopädie', 'Luft- und Raumfahrttechnik', 'Luftverkehrsmanagement', 'Marketing',
  'Maschinenbau', 'Materialwissenschaften', 'Mathematik', 'Mechatronik', 'Mediendesign',
  'Medieninformatik', 'Medienkunst', 'Medienmanagement', 'Medienpädagogik',
  'Medienproduktion', 'Medientechnik', 'Medienwissenschaften', 'Medizin',
  'Medizinische Informatik', 'Medizintechnik', 'Mode', 'Molekularbiologie', 'Musik',
  'Musikmanagement', 'Musikpädagogik', 'Musiktherapie', 'Musikwissenschaft',
  'Nachhaltigkeit', 'Naturheilkunde', 'Naturwissenschaften', 'Nederlandistik',
  'Neogräzistik', 'Neurowissenschaften', 'Önologie', 'Orientalistik', 'Orthopädie',
  'Pädagogik', 'Personalmanagement', 'Pferdewissenschaften', 'Pflegemanagement',
  'Pflegepädagogik', 'Pflegewissenschaft', 'Pharmazie', 'Philologie', 'Philosophie',
  'Physik', 'Physiotherapie', 'Politikwissenschaften', 'Polizei', 'Produktmanagement',
  'Produktionsmanagement', 'Projektmanagement', 'Prozessmanagement', 'Psychologie',
  'Psychotherapie', 'Public Health', 'Public Management', 'Public Relations', 'Publizistik',
  'Qualitätsmanagement', 'Radiologietechnologie', 'Rechnungswesen', 'Religionswissenschaft',
  'Risikomanagement', 'Rohstoffmanagement', 'Romanistik', 'Sales Management', 'Schauspiel',
  'Sinologie', 'Sicherheitsmanagement', 'Skandinavistik', 'Slawistik', 'Software Engineering',
  'Sonderpädagogik', 'Soziale Arbeit', 'Sozialmanagement', 'Sozialpädagogik',
  'Sozialwissenschaften', 'Soziologie', 'Sportgerätetechnik', 'Sportmanagement',
  'Sportmedizin', 'Sportwissenschaft', 'Sprachwissenschaft', 'Stadt- & Regionalentwicklung',
  'Statistik', 'Taxation', 'Tanz', 'Technische Informatik', 'Technische Mathematik',
  'Technisches Management', 'Technische Wissenschaften', 'Theaterwissenschaft', 'Theologie',
  'Tourismusmanagement', 'Tunnelbau', 'Umweltmanagement', 'Umweltpädagogik',
  'Umweltsystemwissenschaften', 'Umwelttechnik', 'Umweltwissenschaften',
  'Unternehmensführung', 'Unternehmensrecht', 'Verfahrenstechnik', 'Verkehrswesen',
  'Versicherung', 'Veterinärmedizin', 'Volkswirtschaftslehre', 'Webdesign',
  'Wirtschaftsberatung', 'Wirtschaftsinformatik', 'Wirtschaftsingenieurwesen',
  'Wirtschaftspädagogik', 'Wirtschaftspsychologie', 'Wirtschaftsrecht',
  'Wirtschaftswissenschaften', 'Wissensmanagement', 'Zahnmedizin', 'Zoologie'
];

// Default job areas as fallback
const DEFAULT_JOB_AREAS = [
  'Büro & Verwaltung',
  'Gastronomie & Service', 
  'IT & Technik',
  'Marketing & Social Media',
  'Nachhilfe & Bildung',
  'Events & Promotion',
  'Verkauf & Einzelhandel',
  'Gesundheit & Pflege',
  'Zusteller',
  'Lager/Logistik',
  'Botendienste'
];

// Internationale Sprachniveaus nach CEFR (Common European Framework of Reference)
const LANGUAGE_LEVELS = [
  { value: 'A1', label: 'A1 - Anfänger' },
  { value: 'A2', label: 'A2 - Grundkenntnisse' },
  { value: 'B1', label: 'B1 - Mittelstufe' },
  { value: 'B2', label: 'B2 - Gute Mittelstufe' },
  { value: 'C1', label: 'C1 - Fortgeschritten' },
  { value: 'C2', label: 'C2 - Muttersprachlich' }
];

// Erweiterte Sprachenliste
const COMMON_LANGUAGES = [
  'Deutsch', 'Englisch', 'Französisch', 'Spanisch', 'Italienisch', 'Portugiesisch',
  'Türkisch', 'Arabisch', 'Russisch', 'Polnisch', 'Rumänisch', 'Ungarisch',
  'Kroatisch', 'Serbisch', 'Bosnisch', 'Slowenisch', 'Slowakisch', 'Tschechisch',
  'Ukrainisch', 'Bulgarisch', 'Griechisch', 'Albanisch', 'Persisch (Farsi)',
  'Chinesisch (Mandarin)', 'Japanisch', 'Koreanisch', 'Hindi', 'Urdu', 'Vietnamesisch',
  'Niederländisch', 'Schwedisch', 'Dänisch', 'Norwegisch', 'Finnisch', 'Hebräisch'
];

// Verfügbarkeits-Optionen
const AVAILABILITY_OPTIONS = [
  { value: 'flexible', label: 'Flexibel (keine Einschränkungen)' },
  { value: 'mornings', label: 'Vormittags (06:00 - 12:00)' },
  { value: 'afternoons', label: 'Nachmittags (12:00 - 18:00)' },
  { value: 'evenings', label: 'Abends (18:00 - 22:00)' },
  { value: 'weekdays', label: 'Nur Werktage (Mo-Fr)' },
  { value: 'weekends', label: 'Nur Wochenende (Sa-So)' },
  { value: 'saturday', label: 'Nur Samstag' },
  { value: 'sunday', label: 'Nur Sonntag' },
  { value: 'mornings_weekdays', label: 'Vormittags an Werktagen' },
  { value: 'afternoons_weekdays', label: 'Nachmittags an Werktagen' },
  { value: 'evenings_weekdays', label: 'Abends an Werktagen' }
];

export default function OnboardingWizard({ user, setUser }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [jobAreas, setJobAreas] = useState(DEFAULT_JOB_AREAS);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    age: '',
    gender: '',
    profileImage: null,
    study: '',
    hasDriverLicense: false,
    hasOwnCar: false,
    languageSkills: [],
    availableHoursPerWeek: '',
    timeConstraints: '',
    residenceState: '',
    residenceCity: '',
    residenceGemeinde: '',
    residenceAddress: '',
    preferredStates: [],
    preferredDistricts: [],
    preferredWorkLocations: [],
    preferredJobAreas: []
  });

  const [studySearch, setStudySearch] = useState('');
  const [newLanguage, setNewLanguage] = useState({ language: '', level: '' });
  const [newWorkLocation, setNewWorkLocation] = useState({ state: '', district: '' });

  // Fetch dynamic job categories from backend
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/job-categories`);
        if (response.data.categories && response.data.categories.length > 0) {
          setJobAreas(response.data.categories);
        }
      } catch (error) {
        console.error('Error fetching categories, using defaults');
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const compressAndUploadImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          const MAX_SIZE = 500;
          if (width > height) {
            if (width > MAX_SIZE) {
              height *= MAX_SIZE / width;
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width *= MAX_SIZE / height;
              height = MAX_SIZE;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressedImage = await compressAndUploadImage(file);
      
      const response = await axios.post(`${API}/users/upload-profile-image`, {
        imageData: compressedImage
      });
      
      setFormData(prev => ({ ...prev, profileImage: response.data.imageUrl }));
      toast.success('Bild hochgeladen!');
    } catch (error) {
      toast.error('Fehler beim Hochladen des Bildes');
    }
  };

  const handleStateToggle = (state) => {
    setFormData(prev => ({
      ...prev,
      preferredStates: prev.preferredStates.includes(state)
        ? prev.preferredStates.filter(s => s !== state)
        : [...prev.preferredStates, state]
    }));
  };

  const handleDistrictToggle = (district) => {
    setFormData(prev => ({
      ...prev,
      preferredDistricts: prev.preferredDistricts.includes(district)
        ? prev.preferredDistricts.filter(d => d !== district)
        : [...prev.preferredDistricts, district]
    }));
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      preferredJobAreas: prev.preferredJobAreas.includes(interest)
        ? prev.preferredJobAreas.filter(i => i !== interest)
        : [...prev.preferredJobAreas, interest]
    }));
  };

  // Österreichische Telefonnummer-Validierung
  const validateAustrianPhone = (phone) => {
    // Entferne Leerzeichen und Bindestriche
    const cleaned = phone.replace(/[\s\-]/g, '');
    
    // Muster für österreichische Nummern:
    // +43 6XX XXX XXXX (Mobilnummern: 0650, 0660, 0664, 0676, 0677, 0678, 0680, 0681, 0688, 0699)
    // +43 1 XXX XXXX (Festnetz Wien)
    // 06XX XXX XXXX (national)
    
    const patterns = [
      /^\+43[67]\d{8,10}$/,  // +43 Mobile (z.B. +436641234567)
      /^\+431\d{7,9}$/,      // +43 Wien Festnetz
      /^\+43[2-5]\d{8,10}$/, // +43 andere Festnetze
      /^06[567]\d{7,9}$/,    // National Mobile (z.B. 06601234567)
      /^01\d{7,9}$/,         // National Wien
      /^0[2-5]\d{8,10}$/,    // National Festnetz
    ];
    
    return patterns.some(pattern => pattern.test(cleaned));
  };

  const handleNext = () => {
    if (step === 1 && (!formData.firstName || !formData.lastName || !formData.phone || !formData.age || !formData.gender)) {
      toast.error('Bitte fülle alle Pflichtfelder aus');
      return;
    }
    // Telefonnummer-Validierung
    if (step === 1 && !validateAustrianPhone(formData.phone)) {
      toast.error('Bitte gib eine gültige österreichische Telefonnummer ein (z.B. +43 660 1234567 oder 0660 1234567)');
      return;
    }
    // Step 2: Profilbild ist jetzt Pflicht
    if (step === 2 && !formData.profileImage) {
      toast.error('Bitte lade ein Profilbild hoch');
      return;
    }
    if (step === 3 && (!formData.study || !formData.availableHoursPerWeek || !formData.timeConstraints)) {
      toast.error('Bitte fülle alle Pflichtfelder aus (Studium, Stunden/Woche, Verfügbarkeit)');
      return;
    }
    if (step === 3 && formData.languageSkills.length === 0) {
      toast.error('Bitte gib mindestens eine Sprache an');
      return;
    }
    setStep(step + 1);
  };

  const handleComplete = async () => {
    if (!formData.residenceState) {
      toast.error('Bitte gib dein Bundesland an (Wohnort)');
      return;
    }
    if (formData.preferredStates.length === 0) {
      toast.error('Bitte wähle mindestens ein Bundesland für Jobs');
      return;
    }
    if (formData.preferredJobAreas.length === 0) {
      toast.error('Bitte wähle mindestens einen Jobbereich');
      return;
    }

    setLoading(true);
    try {
      // Calculate coordinates for proximity sorting
      const coords = getLocationCoordinates(
        formData.residenceState, 
        formData.residenceCity, 
        formData.residenceGemeinde
      );
      
      const profileData = {
        ...formData,
        latitude: coords?.lat || null,
        longitude: coords?.lng || null
      };
      
      const response = await axios.post(`${API}/users/complete-onboarding`, {
        profile: profileData
      });
      
      setUser(response.data);
      toast.success('Profil erstellt! 🎉');
    } catch (error) {
      toast.error('Fehler beim Speichern des Profils');
    } finally {
      setLoading(false);
    }
  };

  const addLanguage = () => {
    if (newLanguage.language && newLanguage.level) {
      setFormData(prev => ({
        ...prev,
        languageSkills: [...prev.languageSkills, {...newLanguage}]
      }));
      setNewLanguage({ language: '', level: '' });
    }
  };

  const removeLanguage = (index) => {
    setFormData(prev => ({
      ...prev,
      languageSkills: prev.languageSkills.filter((_, i) => i !== index)
    }));
  };

  const filteredStudies = AUSTRIAN_STUDIES.filter(study =>
    study.toLowerCase().includes(studySearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-secondary/20 p-4 flex items-center justify-center">
      <div className="w-full max-w-2xl">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">Schritt {step} von 4</span>
            <span className="text-sm font-medium text-primary">{Math.round((step / 4) * 100)}%</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>
        </div>

        {/* Step Content */}
        <Card className="border-slate-100 shadow-[0_8px_24px_rgba(0,0,0,0.08)] rounded-2xl wizard-step">
          <CardContent className="p-8">
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-3">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-3xl font-bold font-outfit mb-2">Willkommen! 👋</h2>
                  <p className="text-slate-600">Lass uns dein Profil einrichten</p>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Vorname</Label>
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                        className="h-12 rounded-xl"
                        placeholder="Max"
                        data-testid="first-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Nachname</Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                        className="h-12 rounded-xl"
                        placeholder="Mustermann"
                        data-testid="last-name-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Handynummer *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="h-12 rounded-xl"
                      placeholder="+43 660 1234567"
                      data-testid="phone-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="age">Alter *</Label>
                      <Input
                        id="age"
                        type="number"
                        min="16"
                        max="99"
                        value={formData.age}
                        onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                        className="h-12 rounded-xl"
                        placeholder="25"
                        data-testid="age-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gender">Geschlecht *</Label>
                      <Select
                        value={formData.gender || undefined}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, gender: value }))}
                      >
                        <SelectTrigger id="gender" className="h-12 rounded-xl" data-testid="gender-select">
                          <SelectValue placeholder="Bitte wählen" />
                        </SelectTrigger>
                        <SelectContent className="z-[100]">
                          <SelectItem value="männlich">Männlich</SelectItem>
                          <SelectItem value="weiblich">Weiblich</SelectItem>
                          <SelectItem value="divers">Divers</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleNext}
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg"
                  data-testid="next-step-button"
                >
                  Weiter <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary/30 mb-3">
                    <Upload className="w-6 h-6 text-secondary-foreground" />
                  </div>
                  <h2 className="text-3xl font-bold font-outfit mb-2">Dein Foto *</h2>
                  <p className="text-slate-600">Ein Profilbild ist erforderlich und erhöht deine Chancen</p>
                </div>

                <div className="flex flex-col items-center space-y-4">
                  {formData.profileImage ? (
                    <div className="relative">
                      <img 
                        src={formData.profileImage} 
                        alt="Profil" 
                        className="w-32 h-32 rounded-full object-cover border-4 border-green-500/50"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute bottom-0 right-0 bg-primary text-white p-2 rounded-full shadow-lg"
                        data-testid="change-image-button"
                      >
                        <Upload className="w-4 h-4" />
                      </button>
                      <div className="absolute -top-1 -right-1 bg-green-500 text-white p-1 rounded-full">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-32 h-32 rounded-full bg-slate-100 border-2 border-dashed border-red-300 flex items-center justify-center hover:border-primary hover:bg-primary/5 transition-colors"
                      data-testid="upload-image-button"
                    >
                      <Upload className="w-8 h-8 text-slate-400" />
                    </button>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-file-input"
                  />
                  
                  <p className="text-sm text-slate-500">
                    {formData.profileImage 
                      ? '✓ Bild hochgeladen - Wird auf 500x500px verkleinert'
                      : 'Klicke hier um ein Foto hochzuladen (Pflichtfeld)'
                    }
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setStep(step - 1)}
                    variant="outline"
                    className="flex-1 h-12 rounded-full"
                    data-testid="back-button"
                  >
                    <ArrowLeft className="mr-2 w-4 h-4" /> Zurück
                  </Button>
                  <Button
                    onClick={handleNext}
                    className="flex-1 h-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg"
                    data-testid="next-step-button"
                  >
                    Weiter <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-3">
                    <GraduationCap className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-3xl font-bold font-outfit mb-2">Studium & Mobilität</h2>
                  <p className="text-slate-600">Was studierst du?</p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="study">Studiengang</Label>
                    <Input
                      id="study"
                      value={studySearch}
                      onChange={(e) => setStudySearch(e.target.value)}
                      className="h-12 rounded-xl"
                      placeholder="Suche..."
                      data-testid="study-search-input"
                    />
                  </div>

                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-xl p-2">
                    {filteredStudies.map((study) => (
                      <button
                        key={study}
                        onClick={() => {
                          setFormData(prev => ({ ...prev, study }));
                          setStudySearch(study);
                        }}
                        className={`w-full text-left px-4 py-2 rounded-lg hover:bg-slate-100 transition-colors ${
                          formData.study === study ? 'bg-primary/10 text-primary font-medium' : ''
                        }`}
                        data-testid={`study-option-${study}`}
                      >
                        {study}
                      </button>
                    ))}
                  </div>

                  <div className="space-y-3 pt-4 border-t border-slate-200">
                    <h4 className="font-medium">Mobilität</h4>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="driver-license"
                        checked={formData.hasDriverLicense}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, hasDriverLicense: checked }))}
                        data-testid="driver-license-checkbox"
                      />
                      <Label htmlFor="driver-license" className="cursor-pointer">Führerschein vorhanden</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="own-car"
                        checked={formData.hasOwnCar}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, hasOwnCar: checked }))}
                        data-testid="own-car-checkbox"
                      />
                      <Label htmlFor="own-car" className="cursor-pointer">Eigener PKW</Label>
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-slate-200">
                    <h4 className="font-medium">Sprachkenntnisse *</h4>
                    <p className="text-xs text-slate-500">Mindestens eine Sprache ist erforderlich</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Select
                        value={newLanguage.language}
                        onValueChange={(value) => setNewLanguage({...newLanguage, language: value})}
                      >
                        <SelectTrigger className="h-10 rounded-xl" data-testid="language-select">
                          <SelectValue placeholder="Sprache wählen" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60 z-[100]">
                          {COMMON_LANGUAGES.map(lang => (
                            <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={newLanguage.level}
                        onValueChange={(value) => setNewLanguage({...newLanguage, level: value})}
                      >
                        <SelectTrigger className="h-10 rounded-xl" data-testid="language-level-select">
                          <SelectValue placeholder="Niveau wählen" />
                        </SelectTrigger>
                        <SelectContent className="z-[100]">
                          {LANGUAGE_LEVELS.map(level => (
                            <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      type="button" 
                      onClick={addLanguage} 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      disabled={!newLanguage.language || !newLanguage.level}
                      data-testid="add-language-button"
                    >
                      + Sprache hinzufügen
                    </Button>
                    {formData.languageSkills.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.languageSkills.map((lang, index) => (
                          <div key={index} className="bg-primary/10 text-primary px-3 py-1.5 rounded-full flex items-center gap-2">
                            <span className="text-sm font-medium">{lang.language} ({lang.level})</span>
                            <button 
                              onClick={() => removeLanguage(index)} 
                              className="text-red-500 hover:text-red-700 font-bold"
                              data-testid={`remove-language-${index}`}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {formData.languageSkills.length === 0 && (
                      <p className="text-xs text-amber-600">Bitte füge mindestens eine Sprache hinzu</p>
                    )}
                  </div>

                  <div className="space-y-3 pt-4 border-t border-slate-200">
                    <h4 className="font-medium">Verfügbarkeit *</h4>
                    <div className="space-y-2">
                      <Label htmlFor="hours">Verfügbare Stunden pro Woche *</Label>
                      <Input
                        id="hours"
                        type="number"
                        min="1"
                        max="40"
                        value={formData.availableHoursPerWeek}
                        onChange={(e) => setFormData(prev => ({ ...prev, availableHoursPerWeek: e.target.value }))}
                        className="h-12 rounded-xl"
                        placeholder="z.B. 20"
                        data-testid="available-hours-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="time-constraints">Wann kannst du arbeiten? *</Label>
                      <Select
                        value={formData.timeConstraints}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, timeConstraints: value }))}
                      >
                        <SelectTrigger className="h-12 rounded-xl" data-testid="time-constraints-select">
                          <SelectValue placeholder="Verfügbarkeit wählen" />
                        </SelectTrigger>
                        <SelectContent className="z-[100]">
                          {AVAILABILITY_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setStep(step - 1)}
                    variant="outline"
                    className="flex-1 h-12 rounded-full"
                    data-testid="back-button"
                  >
                    <ArrowLeft className="mr-2 w-4 h-4" /> Zurück
                  </Button>
                  <Button
                    onClick={handleNext}
                    className="flex-1 h-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg"
                    data-testid="next-step-button"
                  >
                    Weiter <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary/30 mb-3">
                    <MapPin className="w-6 h-6 text-secondary-foreground" />
                  </div>
                  <h2 className="text-3xl font-bold font-outfit mb-2">Wohnort & Jobregion</h2>
                  <p className="text-slate-600">Wo wohnst du und wo möchtest du arbeiten?</p>
                </div>

                <div className="space-y-4">
                  {/* Wohnort Auswahl */}
                  <div className="bg-slate-50 p-4 rounded-xl space-y-4">
                    <ResidenceLocationFields
                      value={formData}
                      onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                      testIdPrefix="residence"
                      showAddress
                      showHeading
                      required
                    />
                  </div>

                  {/* Bevorzugte Arbeitsregionen */}
                  <div className="space-y-2 pt-4 border-t border-slate-200">
                    <Label className="text-base font-semibold">Wo möchtest du arbeiten?</Label>
                    <p className="text-sm text-slate-500 mb-2">Wähle Bundesländer aus oder füge spezifische Bezirke hinzu</p>
                    <PreferredWorkLocationsField
                      preferredStates={formData.preferredStates}
                      preferredWorkLocations={formData.preferredWorkLocations || []}
                      onToggleState={handleStateToggle}
                      onAddLocation={(loc) => setFormData(prev => {
                        const list = prev.preferredWorkLocations || [];
                        const exists = list.some(l => l.state === loc.state && l.district === loc.district);
                        return exists ? prev : { ...prev, preferredWorkLocations: [...list, loc] };
                      })}
                      onRemoveLocation={(idx) => setFormData(prev => ({
                        ...prev,
                        preferredWorkLocations: (prev.preferredWorkLocations || []).filter((_, i) => i !== idx)
                      }))}
                      variant="onboarding"
                      testIdPrefix="state"
                    />
                  </div>

                  {/* Legacy District Selection - hidden but functional for backwards compatibility */}
                  {formData.preferredStates.length > 0 && false && (
                    <div className="space-y-2">
                      <Label>Bezirke (optional)</Label>
                      <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                        {formData.preferredStates.flatMap(state => 
                          getDistrictsForState(state) || []
                        ).map((district) => (
                          <button
                            key={district}
                            onClick={() => handleDistrictToggle(district)}
                            className={`px-3 py-1 rounded-full text-sm transition-all ${
                              formData.preferredDistricts.includes(district)
                                ? 'bg-secondary text-secondary-foreground'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                            data-testid={`district-${district}`}
                          >
                            {district}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 pt-4 border-t border-slate-200">
                    <Label>Bevorzugte Jobbereiche</Label>
                    <JobAreasField
                      selected={formData.preferredJobAreas}
                      available={jobAreas}
                      onToggle={handleInterestToggle}
                      loading={loadingCategories}
                      testIdPrefix="job-area"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setStep(step - 1)}
                    variant="outline"
                    className="flex-1 h-12 rounded-full"
                    data-testid="back-button"
                  >
                    <ArrowLeft className="mr-2 w-4 h-4" /> Zurück
                  </Button>
                  <Button
                    onClick={handleComplete}
                    disabled={loading}
                    className="flex-1 h-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg"
                    data-testid="complete-onboarding-button"
                  >
                    {loading ? 'Speichere...' : (
                      <>
                        Fertig <Sparkles className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
